/*
 * <cart-discount-form> — promo-code entry inside the cart summary.
 *
 * Applies and removes codes through POST /cart/update ({ discount: 'A,B' }) and
 * repaints the cart sections from the same response, so the shopper sees the
 * discount land on the summary instead of discovering it at checkout.
 *
 * Two things about that endpoint shape this file:
 *   1. `discount` REPLACES the cart's codes rather than adding to them, so
 *      every write has to carry the full set the cart should end up with.
 *   2. Themes can't read the entered codes from Liquid, and a code can apply
 *      without producing a visible summary row (free shipping, say). So the
 *      set is read once from /cart.js and kept fresh from each response,
 *      rather than being scraped back out of the DOM.
 *
 * The applied code is shown by the summary's own discount row — the row owns
 * the remove button, which is why removal is delegated from the document.
 */
class CartDiscountForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-discount-input]');
    this.submitButton = this.querySelector('[data-discount-submit]');
    this.toggleButton = this.querySelector('[data-discount-toggle]');
    this.panel = this.querySelector('[data-discount-panel]');
    this.messageElement = this.querySelector('[data-discount-message]');

    // Codes the cart is holding. Null until read from /cart.js.
    this.codes = null;

    this.toggleButton.addEventListener('click', () => this.open());

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onApply();
    });

    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.close();
    });
  }

  open() {
    this.panel.hidden = false;
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.input.focus();
  }

  close() {
    this.panel.hidden = true;
    this.toggleButton.setAttribute('aria-expanded', 'false');
    this.input.value = '';
    this.setMessage('');
    this.toggleButton.focus();
  }

  /* The cart's current codes, read from /cart.js the first time they're asked for. */
  getCodes() {
    if (this.codes) return Promise.resolve(this.codes);

    return fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        this.codes = codesFrom(cart);
        return this.codes;
      });
  }

  onApply() {
    const code = this.input.value.trim();
    if (!code) return;

    this.setLoading(true);
    this.setMessage('');

    return this.getCodes()
      .then((codes) => {
        if (codes.some((applied) => sameCode(applied, code))) {
          this.setMessage(this.dataset.errorDuplicate, 'error');
          return null;
        }

        return this.write(codes.concat(code)).then((state) => {
          const applicable = state.discount_codes;

          // No discount_codes in the payload means the platform isn't reporting
          // applicability; take the repaint at face value rather than reverting.
          if (!Array.isArray(applicable) || applicable.some((entry) => entry.applicable && sameCode(entry.code, code))) {
            this.close();
            this.confirmApplied(code);
            return state;
          }

          // Rejected: put the cart back the way we found it, so a dead code
          // can't ride along to checkout, then say what happened.
          this.setMessage(this.dataset.errorInvalid, 'error');
          return this.write(codes);
        });
      })
      .catch((error) => this.reportFailure(error))
      .finally(() => this.setLoading(false));
  }

  onRemove(code) {
    this.setLoading(true);
    this.setMessage('');

    return this.getCodes()
      .then((codes) => this.write(codes.filter((applied) => !sameCode(applied, code))))
      .catch((error) => this.reportFailure(error))
      .finally(() => this.setLoading(false));
  }

  /* A code can apply without earning a summary row; say so rather than going silent. */
  confirmApplied(code) {
    const footer = this.closest('#main-cart-footer') || document;
    const hasRow = Array.from(footer.querySelectorAll('[data-discount-code]')).some((row) =>
      sameCode(row.dataset.discountCode, code)
    );

    if (!hasRow) this.setMessage(this.dataset.successApplied, 'success');
  }

  reportFailure(error) {
    console.error(error);
    this.setMessage(error.cartMessage || this.dataset.errorGeneric, 'error');
  }

  /* Write the full set of codes the cart should end up with, then repaint. */
  write(codes) {
    const body = {
      discount: codes.join(','),
      sections: this.sectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    };

    return fetch(routes.cart_update_url, { ...fetchConfig(), ...{ body: JSON.stringify(body) } })
      .then((response) =>
        response.text().then((text) => {
          let state = {};
          try {
            state = JSON.parse(text);
          } catch (parseError) {
            state = {};
          }

          if (!response.ok) {
            const error = new Error(`Cart update failed with ${response.status}`);
            error.cartMessage = state.description || state.message || '';
            throw error;
          }

          return state;
        })
      )
      .then((state) => {
        this.codes = codesFrom(state) || codes;
        this.renderSections(state);
        return state;
      });
  }

  sectionsToRender() {
    const sections = [];
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');

    if (items) sections.push({ id: 'main-cart-items', section: items.dataset.id, selector: '.js-contents' });
    if (footer) sections.push({ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' });
    sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });

    return sections;
  }

  renderSections(state) {
    if (!state.sections) return;

    this.sectionsToRender().forEach((section) => {
      const markup = state.sections[section.section];
      if (!markup) return;

      const source = new DOMParser().parseFromString(markup, 'text/html').querySelector(section.selector);
      const container = document.getElementById(section.id);
      if (!source || !container) return;

      const target = container.querySelector(section.selector) || container;
      target.innerHTML = source.innerHTML;

      // This component sits inside the footer's .js-contents, so the repaint
      // above just replaced it with a fresh, closed copy. Its markup doesn't
      // depend on cart state, so put the live node back and keep whatever the
      // shopper had open or typed.
      if (section.id === 'main-cart-footer') {
        const replacement = target.querySelector('cart-discount-form');
        if (replacement) replacement.replaceWith(this);
      }
    });
  }

  setLoading(isLoading) {
    this.classList.toggle('is-loading', isLoading);
    this.input.disabled = isLoading;
    this.submitButton.disabled = isLoading;
  }

  setMessage(message, tone) {
    this.messageElement.textContent = message || '';
    this.messageElement.classList.toggle('is-error', tone === 'error');
    this.messageElement.classList.toggle('is-success', tone === 'success');
  }
}

function codesFrom(cart) {
  if (!Array.isArray(cart.discount_codes)) return null;
  return cart.discount_codes.map((entry) => entry.code);
}

function sameCode(a, b) {
  return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
}

// The remove buttons live on the summary's discount rows, which are repainted
// independently of this component — so delegate from the document once.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-discount-remove]');
  if (!button) return;

  const scope = button.closest('#main-cart-footer') || document;
  const form = scope.querySelector('cart-discount-form');
  if (!form) return;

  event.preventDefault();
  form.onRemove(button.dataset.discountRemove);
});

if (!customElements.get('cart-discount-form')) {
  customElements.define('cart-discount-form', CartDiscountForm);
}
